import type { ExerciseDef } from './types'

// ─── 9.5 Encoders & decoders ──────────────────────────────────────────────────

export const masks: ExerciseDef = {
  starter: `// A mask says which keys each query may look at: 1 = allowed, 0 = hidden.

// 1. causalMask(n): an n×n mask where token i can see tokens 0 … i (itself and the past).
function causalMask(n: number): number[][] {
  return []
}

// 2. maskedSoftmax(scores, mask): softmax of each row, but hidden positions get probability exactly 0.
//    (Treat a hidden score as −Infinity: e^−∞ = 0.)
function maskedSoftmax(scores: number[][], mask: number[][]): number[][] {
  return scores
}

console.log(causalMask(4))
console.log(maskedSoftmax([[1, 2, 3], [1, 2, 3], [1, 2, 3]], causalMask(3)).map(r => r.map(v => round(v, 3))))
`,
  solution: `function causalMask(n: number): number[][] {
  return range(n).map(i => range(n).map(j => (j <= i ? 1 : 0)))
}

function maskedSoftmax(scores: number[][], mask: number[][]): number[][] {
  return scores.map((row, i) => {
    const allowed = row.filter((_, j) => mask[i][j] === 1)
    const m = Math.max(...allowed)
    const e = row.map((s, j) => (mask[i][j] === 1 ? Math.exp(s - m) : 0))
    const total = vec.sum(e)
    return e.map(v => v / total)
  })
}

console.log(causalMask(4))
console.log(maskedSoftmax([[1, 2, 3], [1, 2, 3], [1, 2, 3]], causalMask(3)).map(r => r.map(v => round(v, 3))))
`,
  tests: `
test("causalMask(3) is lower-triangular", () => expect(causalMask(3), "causalMask(3)").toEqual([[1, 0, 0], [1, 1, 0], [1, 1, 1]]))
test("the first token can only attend to itself", () => expect(maskedSoftmax([[5, 1, 2]], [[1, 0, 0]])[0], "row 0").toEqual([1, 0, 0]))
test("hidden positions get exactly 0", () => expect(maskedSoftmax([[1, 9, 1]], [[1, 0, 1]])[0][1], "hidden weight").toBe(0))
test("each row still sums to 1", () => expect(maskedSoftmax([[1, 2, 3], [0, 0, 0], [3, 1, 2]], causalMask(3)).map(r => vec.sum(r)), "row sums").toBeCloseTo([1, 1, 1]))
test("a full mask is ordinary softmax", () => expect(maskedSoftmax([[1, 2]], [[1, 1]])[0], "row").toBeCloseTo(vec.softmax([1, 2])))
`,
}

export const crossAttention: ExerciseDef = {
  starter: `// Cross-attention: queries come from the decoder (the sentence being written),
// keys and values from the encoder (the sentence being read).
//   Q: m × d   (one row per decoder token)      K, V: n × d   (one row per encoder token)
// attend(Q, K, V) = softmax(Q·Kᵀ / √d) · V   → m × d
// Also return the m × n attention weights so we can look at them.
function attend(Q: number[][], K: number[][], V: number[][]): { out: number[][]; weights: number[][] } {
  return { out: Q, weights: [] }
}

// Toy "translation": decoder words should find their matching encoder word.
const encoder = { le: [1, 0, 0], chat: [0, 1, 0], dort: [0, 0, 1] }
const K = Object.values(encoder).map(v => vec.scale(v, 4)), V = Object.values(encoder)
const Q = [[1, 0, 0], [0, 1, 0], [0, 0, 1]].map(v => vec.scale(v, 4))   // "the", "cat", "sleeps"
const { weights } = attend(Q, K, V)
plot.heatmap(weights, { title: "who looks at whom", rowLabels: ["the", "cat", "sleeps"], colLabels: ["le", "chat", "dort"] })
`,
  solution: `function attend(Q: number[][], K: number[][], V: number[][]): { out: number[][]; weights: number[][] } {
  const d = Q[0].length
  const scores = mat.scale(mat.mul(Q, mat.transpose(K)), 1 / Math.sqrt(d))
  const weights = scores.map(row => vec.softmax(row))
  return { out: mat.mul(weights, V), weights }
}

const encoder = { le: [1, 0, 0], chat: [0, 1, 0], dort: [0, 0, 1] }
const K = Object.values(encoder).map(v => vec.scale(v, 4)), V = Object.values(encoder)
const Q = [[1, 0, 0], [0, 1, 0], [0, 0, 1]].map(v => vec.scale(v, 4))
const { weights } = attend(Q, K, V)
plot.heatmap(weights, { title: "who looks at whom", rowLabels: ["the", "cat", "sleeps"], colLabels: ["le", "chat", "dort"] })
`,
  tests: `
test("2 decoder tokens attending over 5 encoder tokens: weights are 2 × 5", () => {
  const __r = attend(mat.random(2, 4), mat.random(5, 4), mat.random(5, 3))
  expect([__r.weights.length, __r.weights[0].length], "weights shape").toEqual([2, 5])
})
test("…and the output is 2 rows of the value size", () => {
  const __r = attend(mat.random(2, 4), mat.random(5, 4), mat.random(5, 3))
  expect([__r.out.length, __r.out[0].length], "out shape").toEqual([2, 3])
})
test("every row of weights sums to 1", () => expect(attend(mat.random(3, 2), mat.random(4, 2), mat.random(4, 2)).weights.map(r => vec.sum(r)), "row sums").toBeCloseTo([1, 1, 1]))
test("'cat' looks mostly at 'chat'", () => expect(vec.argmax(weights[1]), "where row 'cat' looks").toBe(1))
test("scores are scaled by 1/√d", () => {
  const __w = attend([[2, 0]], [[1, 0], [0, 1]], [[1], [0]]).weights[0]
  expect(__w[0], "weight").toBeCloseTo(Math.exp(2 / Math.SQRT2) / (Math.exp(2 / Math.SQRT2) + 1))
})
`,
}

export const maskedLM: ExerciseDef = {
  starter: `// BERT learns by filling in blanks. makeExample(words, positions): replace the words at those positions
// with "[MASK]", and return the hidden words as the targets the model must recover.
//   makeExample(["the", "cat", "sat"], [1]) → { input: ["the", "[MASK]", "sat"], targets: ["cat"] }
function makeExample(words: string[], positions: number[]): { input: string[]; targets: string[] } {
  return { input: words, targets: [] }
}

// pickPositions(n, rate): choose round(n × rate) DIFFERENT random positions out of 0 … n−1, in increasing order.
function pickPositions(n: number, rate: number): number[] {
  return []
}

random.seed(3)
const sentence = "the little fox hides in the garden near the old farmer".split(" ")
const ex = makeExample(sentence, pickPositions(sentence.length, 0.15))
console.log(ex.input.join(" "), "  →  targets:", ex.targets)
`,
  solution: `function makeExample(words: string[], positions: number[]): { input: string[]; targets: string[] } {
  const hidden = new Set(positions)
  return {
    input: words.map((w, i) => (hidden.has(i) ? "[MASK]" : w)),
    targets: positions.map(i => words[i]),
  }
}

function pickPositions(n: number, rate: number): number[] {
  return random.shuffle(range(n)).slice(0, Math.round(n * rate)).sort((a, b) => a - b)
}

random.seed(3)
const sentence = "the little fox hides in the garden near the old farmer".split(" ")
const ex = makeExample(sentence, pickPositions(sentence.length, 0.15))
console.log(ex.input.join(" "), "  →  targets:", ex.targets)
`,
  tests: `
test("hides the chosen words", () => expect(makeExample(["the", "cat", "sat"], [1]), "makeExample").toEqual({ input: ["the", "[MASK]", "sat"], targets: ["cat"] }))
test("several masks, targets in order", () => expect(makeExample(["a", "b", "c", "d"], [0, 3]).targets, "targets").toEqual(["a", "d"]))
test("picks the right number of positions", () => expect(pickPositions(20, 0.15), "pickPositions(20, 0.15)").toHaveLength(3))
test("positions are different, sorted and in range", () => {
  const __p = pickPositions(10, 0.5)
  expect(__p.every((v, i) => v >= 0 && v < 10 && (i === 0 || v > __p[i - 1])), "distinct, increasing, in range").toBe(true)
})
`,
}

// ─── 9.6 Training at scale ────────────────────────────────────────────────────

export const lrSchedule: ExerciseDef = {
  starter: `// lrAt(step, total, warmup, maxLr): the learning rate at a given step.
//  • warm-up: for step < warmup, rise in a straight line: maxLr × (step + 1) / warmup
//  • then cosine decay from maxLr down to 10% of maxLr at the last step:
//      p = (step − warmup) / (total − warmup)          (0 → 1)
//      lr = minLr + (maxLr − minLr) × ½ × (1 + cos(π · p)),   with minLr = 0.1 × maxLr
function lrAt(step: number, total: number, warmup: number, maxLr: number): number {
  return maxLr
}

plot.line(range(1000).map(s => lrAt(s, 1000, 100, 3e-4)), { title: "warm-up, then cosine decay", xLabel: "step" })
`,
  solution: `function lrAt(step: number, total: number, warmup: number, maxLr: number): number {
  if (step < warmup) return (maxLr * (step + 1)) / warmup
  const p = (step - warmup) / (total - warmup)
  const minLr = 0.1 * maxLr
  return minLr + (maxLr - minLr) * 0.5 * (1 + Math.cos(Math.PI * p))
}

plot.line(range(1000).map(s => lrAt(s, 1000, 100, 3e-4)), { title: "warm-up, then cosine decay", xLabel: "step" })
`,
  tests: `
test("starts small during warm-up", () => expect(lrAt(0, 1000, 100, 1), "lrAt(0, …)").toBeCloseTo(0.01))
test("halfway through warm-up it's at half", () => expect(lrAt(49, 1000, 100, 1), "lrAt(49, …)").toBeCloseTo(0.5))
test("peaks at maxLr when warm-up ends", () => expect(lrAt(100, 1000, 100, 1), "lrAt(100, …)").toBeCloseTo(1))
test("halfway through the decay it's at 55%", () => expect(lrAt(550, 1000, 100, 1), "lrAt(550, …)").toBeCloseTo(0.55))
test("ends at 10%", () => expect(lrAt(1000, 1000, 100, 1), "lrAt(1000, …)").toBeCloseTo(0.1))
`,
}

export const clipping: ExerciseDef = {
  starter: `// A single bad batch can produce a huge gradient and wreck a long training run.
// Gradient clipping caps the size of the whole update.

// 1. globalNorm(grads): the length of ALL the gradients together, as if they were one long vector:
//    √(sum of every gradient entry squared)
function globalNorm(grads: number[][]): number {
  return 0
}

// 2. clip(grads, maxNorm): if the global norm is above maxNorm, scale EVERY entry by maxNorm / norm.
//    Otherwise return the gradients unchanged. (Return new arrays; don't modify the input.)
function clip(grads: number[][], maxNorm: number): number[][] {
  return grads
}

const g = [[3, 0], [0, 4, 0]]
console.log(globalNorm(g), clip(g, 1), clip(g, 10))
`,
  solution: `function globalNorm(grads: number[][]): number {
  let s = 0
  for (const g of grads) for (const v of g) s += v * v
  return Math.sqrt(s)
}

function clip(grads: number[][], maxNorm: number): number[][] {
  const n = globalNorm(grads)
  const f = n > maxNorm ? maxNorm / n : 1
  return grads.map(g => g.map(v => v * f))
}

const g = [[3, 0], [0, 4, 0]]
console.log(globalNorm(g), clip(g, 1), clip(g, 10))
`,
  tests: `
test("globalNorm of [[3, 0], [0, 4, 0]] is 5", () => expect(globalNorm([[3, 0], [0, 4, 0]]), "globalNorm").toBeCloseTo(5))
test("clipping to 1 shrinks everything by 5", () => expect(clip([[3, 0], [0, 4, 0]], 1), "clip(…, 1)").toBeCloseTo([[0.6, 0], [0, 0.8, 0]]))
test("small gradients are left alone", () => expect(clip([[0.1, 0.2]], 10), "clip(…, 10)").toBeCloseTo([[0.1, 0.2]]))
test("the direction is kept", () => {
  const __c = clip([[6, 8]], 1)
  expect(__c[0][0] / __c[0][1], "ratio").toBeCloseTo(0.75)
})
test("the input isn't modified", () => {
  const __g = [[30, 40]]
  clip(__g, 1)
  expect(__g, "input afterwards").toEqual([[30, 40]])
})
`,
}

export const accumulation: ExerciseDef = {
  starter: `// When a batch is too big for one GPU, split it into micro-batches (or across many GPUs),
// compute each one's gradient, and ADD them up before taking one step. The maths gives the same answer.
random.seed(2)
const X = Tensor.randn(16, 5)
const y = range(16).map(i => i % 3)
const layer = new Linear(5, 3)

// 1. The full batch, in one go:
layer.W.zeroGrad()
layer.forward(X).crossEntropy(y).backward()
const fullGrad = Array.from(layer.W.grad)

// 2. accumulate(k): zero the gradient ONCE, then for each of k equal micro-batches compute its loss,
//    multiply it by 1/k (each micro-batch loss is already a mean over its own rows) and call backward —
//    WITHOUT zeroing in between, so the gradients add up. Return a copy of layer.W.grad.
//    Hint: rows i·s … (i+1)·s − 1 of X are Tensor.from(X.toArray().slice(i * s, (i + 1) * s)).
function accumulate(k: number): number[] {
  layer.W.zeroGrad()
  return Array.from(layer.W.grad)
}

const viaFour = accumulate(4)
console.log("biggest difference:", Math.max(...fullGrad.map((g, i) => Math.abs(g - viaFour[i]))))
`,
  solution: `random.seed(2)
const X = Tensor.randn(16, 5)
const y = range(16).map(i => i % 3)
const layer = new Linear(5, 3)

layer.W.zeroGrad()
layer.forward(X).crossEntropy(y).backward()
const fullGrad = Array.from(layer.W.grad)

function accumulate(k: number): number[] {
  layer.W.zeroGrad()
  const s = 16 / k
  for (let i = 0; i < k; i++) {
    const xb = Tensor.from(X.toArray().slice(i * s, (i + 1) * s))
    layer.forward(xb).crossEntropy(y.slice(i * s, (i + 1) * s)).mul(1 / k).backward()
  }
  return Array.from(layer.W.grad)
}

const viaFour = accumulate(4)
console.log("biggest difference:", Math.max(...fullGrad.map((g, i) => Math.abs(g - viaFour[i]))))
`,
  tests: `
test("4 micro-batches give the full-batch gradient", () => expect(accumulate(4), "accumulated gradient").toBeCloseTo(fullGrad, 1e-12))
test("so do 2, or 16", () => {
  expect(accumulate(2), "k = 2").toBeCloseTo(fullGrad, 1e-12)
  expect(accumulate(16), "k = 16").toBeCloseTo(fullGrad, 1e-12)
})
test("the gradient isn't all zeros", () => expect(accumulate(4).some(v => Math.abs(v) > 1e-6), "non-zero").toBe(true))
`,
}

export const computeBudget: ExerciseDef = {
  starter: `// Back-of-the-envelope maths every AI lab does before a training run.

// 1. trainingFlops(params, tokens): about 6 operations per parameter per token
//    (2 for the forward pass, 4 for the backward pass).
function trainingFlops(params: number, tokens: number): number {
  return 0
}

// 2. trainingMemoryGB(params): mixed-precision Adam keeps 16 bytes per parameter
//    (weights, gradients, a full-precision copy and Adam's two averages). 1 GB = 1e9 bytes.
function trainingMemoryGB(params: number): number {
  return 0
}

// 3. days(flops, gpus, flopsPerGpu): how many days a cluster needs. (86 400 seconds in a day.)
function days(flops: number, gpus: number, flopsPerGpu: number): number {
  return 0
}

const gpt3 = trainingFlops(175e9, 300e9)
console.log("GPT-3:", gpt3.toExponential(2), "FLOPs,", round(trainingMemoryGB(175e9)), "GB of training state")
console.log("…on 1 000 GPUs at 400 TFLOP/s each:", round(days(gpt3, 1000, 400e12), 1), "days")
`,
  solution: `function trainingFlops(params: number, tokens: number): number {
  return 6 * params * tokens
}

function trainingMemoryGB(params: number): number {
  return (16 * params) / 1e9
}

function days(flops: number, gpus: number, flopsPerGpu: number): number {
  return flops / (gpus * flopsPerGpu) / 86400
}

const gpt3 = trainingFlops(175e9, 300e9)
console.log("GPT-3:", gpt3.toExponential(2), "FLOPs,", round(trainingMemoryGB(175e9)), "GB of training state")
console.log("…on 1 000 GPUs at 400 TFLOP/s each:", round(days(gpt3, 1000, 400e12), 1), "days")
`,
  tests: `
test("GPT-3 took about 3.15 × 10²³ FLOPs", () => expect(trainingFlops(175e9, 300e9), "flops").toBeCloseTo(3.15e23, 1e21))
test("a 7-billion-parameter model needs 112 GB of training state", () => expect(trainingMemoryGB(7e9), "GB").toBeCloseTo(112))
test("1e20 FLOPs on 10 GPUs at 1e14 FLOP/s is about 1.16 days", () => expect(days(1e20, 10, 1e14), "days").toBeCloseTo(1e20 / 1e15 / 86400))
`,
}

// ─── 9.8 Project: your own GPT ────────────────────────────────────────────────

const GPT_DATA = `// Your training text. Try pasting your own (a few thousand characters is plenty)!
const text = data.stories.replace(/\\s+/g, " ")`

const GPT_DATA_SOLUTION = `const chars = [...new Set(text)].sort()
const encode = (s: string) => [...s].map(c => chars.indexOf(c))
const decode = (ids: number[]) => ids.map(i => chars[i]).join("")
const ids = encode(text)
const cut = Math.floor(ids.length * 0.9)
const trainIds = ids.slice(0, cut)
const valIds = ids.slice(cut)`

const GPT_MODEL = `// A small GPT, assembled from the pieces you built in this module.
function makeGPT(V: number, T: number, C = 32, L = 2, H = 4) {
  const hs = C / H
  const r = (a: number, b: number, s = 0.08) => Tensor.randn(a, b, s)
  const tok = r(V, C), pos = r(T, C)
  const blocks = range(L).map(() => ({
    g1: Tensor.ones(1, C), b1: Tensor.zeros(1, C),
    heads: range(H).map(() => ({ q: r(C, hs), k: r(C, hs), v: r(C, hs) })),
    wo: r(C, C, 0.04), g2: Tensor.ones(1, C), b2: Tensor.zeros(1, C),
    w1: r(C, 4 * C), w2: r(4 * C, C, 0.04),
  }))
  const gf = Tensor.ones(1, C), bf = Tensor.zeros(1, C), out = r(C, V)
  const params = [tok, pos, gf, bf, out, ...blocks.flatMap(b => [b.g1, b.b1, b.wo, b.g2, b.b2, b.w1, b.w2, ...b.heads.flatMap(h => [h.q, h.k, h.v])])]
  function forward(x: number[]): Tensor {
    let h = tok.gather(x).add(pos.gather(range(x.length)))
    for (const b of blocks) {
      const n = h.layerNorm(b.g1, b.b1)
      const heads = b.heads.map(w => n.matmul(w.q).matmul(n.matmul(w.k).transpose()).mul(1 / Math.sqrt(hs)).maskCausal().softmax().matmul(n.matmul(w.v)))
      h = h.add(Tensor.concatCols(heads).matmul(b.wo))
      h = h.add(h.layerNorm(b.g2, b.b2).matmul(b.w1).gelu().matmul(b.w2))
    }
    return h.layerNorm(gf, bf).matmul(out)
  }
  return { forward, params }
}`

const GPT_TRAIN_SOLUTION = `function lrAt(step: number, total: number, warmup: number, maxLr: number): number {
  if (step < warmup) return (maxLr * (step + 1)) / warmup
  const p = (step - warmup) / (total - warmup)
  return 0.1 * maxLr + 0.9 * maxLr * 0.5 * (1 + Math.cos(Math.PI * p))
}

function clipGrads(params: Tensor[], maxNorm: number): void {
  let s = 0
  for (const p of params) for (const g of p.grad) s += g * g
  const norm = Math.sqrt(s)
  if (norm > maxNorm) for (const p of params) for (let i = 0; i < p.grad.length; i++) p.grad[i] *= maxNorm / norm
}

function valLoss(): number {
  let total = 0, n = 0
  for (let i = 0; i + T + 1 < valIds.length; i += T) {
    total += gpt.forward(valIds.slice(i, i + T)).crossEntropy(valIds.slice(i + 1, i + T + 1)).item()
    n++
  }
  return total / n
}

const STEPS = 400
const opt = new Adam(gpt.params, { lr: 5e-3 })
const chart = plot.live("loss")
let best = Infinity
for (let step = 0; step < STEPS; step++) {
  opt.lr = lrAt(step, STEPS, 40, 5e-3)
  let loss = Tensor.scalar(0)
  for (let b = 0; b < 4; b++) {
    const i = random.int(0, trainIds.length - T - 1)
    loss = loss.add(gpt.forward(trainIds.slice(i, i + T)).crossEntropy(trainIds.slice(i + 1, i + T + 1)).mul(0.25))
  }
  opt.zeroGrad()
  loss.backward()
  clipGrads(gpt.params, 1)
  opt.step()
  if (step % 50 === 49) {
    const v = valLoss()
    best = Math.min(best, v)
    chart.push({ train: loss.item(), validation: v })
  }
}`

export const gptData: ExerciseDef = {
  starter: `${GPT_DATA}

// 1. A character-level tokenizer: the vocabulary is every distinct character, sorted.
const chars: string[] = []
const encode = (s: string): number[] => []
const decode = (ids: number[]): string => ""

// 2. Split the token ids: the first 90% for training, the last 10% for validation.
const ids = encode(text)
const trainIds: number[] = []
const valIds: number[] = []

// 3. A baseline to beat: a model that only knows how common each character is (add-one smoothing).
function unigramLoss(): number {
  const counts = new Array(chars.length).fill(1)
  for (const i of trainIds) counts[i]++
  const total = vec.sum(counts)
  return vec.mean(valIds.map(i => -Math.log(counts[i] / total)))
}

console.log(text.length, "characters,", chars.length, "distinct:", JSON.stringify(chars.join("")))
console.log("baseline validation loss:", round(unigramLoss(), 3), " (uniform guessing would be", round(Math.log(chars.length), 3) + ")")
`,
  solution: `${GPT_DATA}

${GPT_DATA_SOLUTION}

function unigramLoss(): number {
  const counts = new Array(chars.length).fill(1)
  for (const i of trainIds) counts[i]++
  const total = vec.sum(counts)
  return vec.mean(valIds.map(i => -Math.log(counts[i] / total)))
}

console.log(text.length, "characters,", chars.length, "distinct:", JSON.stringify(chars.join("")))
console.log("baseline validation loss:", round(unigramLoss(), 3), " (uniform guessing would be", round(Math.log(chars.length), 3) + ")")
`,
  tests: `
test("the vocabulary is the sorted distinct characters", () => expect(chars, "chars").toEqual([...new Set(text)].sort()))
test("decode(encode(s)) gives s back", () => expect(decode(encode("the cat")), "round trip").toBe("the cat"))
test("90% / 10% split, nothing lost", () => {
  expect(trainIds.length + valIds.length, "total").toBe(ids.length)
  expect(trainIds.length, "train size").toBe(Math.floor(ids.length * 0.9))
})
test("the validation part is the END of the text", () => expect(decode(valIds), "validation text").toBe(text.slice(Math.floor(ids.length * 0.9))))
`,
}

export const gptTrain: ExerciseDef = {
  starter: `${GPT_DATA}
${GPT_DATA_SOLUTION}

${GPT_MODEL}

random.seed(1)
const T = 24
const gpt = makeGPT(chars.length, T)
console.log("parameters:", gpt.params.reduce((s, p) => s + p.data.length, 0))

// 1. lrAt: warm-up then cosine decay, from the training-at-scale lesson.
function lrAt(step: number, total: number, warmup: number, maxLr: number): number {
  return maxLr
}

// 2. clipGrads(params, maxNorm): global-norm clipping, done IN PLACE on every p.grad.
function clipGrads(params: Tensor[], maxNorm: number): void {}

// 3. valLoss(): the average loss over the validation text, cut into consecutive windows of T characters.
function valLoss(): number {
  return Infinity
}

// 4. The loop: 400 steps; each step averages the loss of 4 random windows of the training text.
//    Set opt.lr from lrAt (warm-up 40 steps, max 5e-3), backward, clip to 1, step.
//    Every 50 steps, push the train and validation loss to the chart and remember the best validation loss.
const STEPS = 400
const opt = new Adam(gpt.params, { lr: 5e-3 })
const chart = plot.live("loss")
let best = Infinity
for (let step = 0; step < STEPS; step++) {
  // your training step here
}
console.log("best validation loss:", round(best, 3))
`,
  solution: `${GPT_DATA}
${GPT_DATA_SOLUTION}

${GPT_MODEL}

random.seed(1)
const T = 24
const gpt = makeGPT(chars.length, T)
console.log("parameters:", gpt.params.reduce((s, p) => s + p.data.length, 0))

${GPT_TRAIN_SOLUTION}
console.log("best validation loss:", round(best, 3))
`,
  tests: `
test("lrAt peaks at maxLr after warm-up", () => expect(lrAt(40, 400, 40, 5e-3), "lrAt(40, …)").toBeCloseTo(5e-3))
test("clipGrads caps the global norm", () => {
  const __p = Tensor.zeros(1, 2)
  __p.grad[0] = 30
  __p.grad[1] = 40
  clipGrads([__p], 5)
  expect(Array.from(__p.grad), "clipped grad").toBeCloseTo([3, 4])
})
test("the GPT clearly beats the letter-frequency baseline (~2.77)", () => expect(best, "best validation loss").toBeLessThan(2.3))
`,
}

export const gptGenerate: ExerciseDef = {
  starter: `${GPT_DATA}
${GPT_DATA_SOLUTION}

${GPT_MODEL}

random.seed(1)
const T = 24
const gpt = makeGPT(chars.length, T)
${GPT_TRAIN_SOLUTION}

// 1. generate(prompt, n, temperature, topK): add n characters to the prompt, one at a time.
//    Feed the model the last T tokens; take the scores in its LAST row; keep only the topK highest
//    (set the rest to −Infinity); softmax with the temperature; sample; append.
function generate(prompt: string, n: number, temperature: number, topK: number): string {
  return prompt
}

// 2. perplexity: e^(validation loss) — "how many characters the model is choosing between, on average".
const perplexity = 0

console.log("perplexity:", round(perplexity, 2), " (out of", chars.length, "characters)")
for (const t of [0.5, 1]) console.log(\`T = \${t}:\`, JSON.stringify(generate("the ", 80, t, 8)))
`,
  solution: `${GPT_DATA}
${GPT_DATA_SOLUTION}

${GPT_MODEL}

random.seed(1)
const T = 24
const gpt = makeGPT(chars.length, T)
${GPT_TRAIN_SOLUTION}

function generate(prompt: string, n: number, temperature: number, topK: number): string {
  const s = encode(prompt)
  for (let i = 0; i < n; i++) {
    const ctx = s.slice(-T)
    const scores = gpt.forward(ctx).row(ctx.length - 1)
    const cutoff = [...scores].sort((a, b) => b - a)[Math.min(topK, scores.length) - 1]
    const kept = scores.map(v => (v >= cutoff ? v : -Infinity))
    s.push(random.sample(vec.softmax(kept, temperature)))
  }
  return decode(s)
}

const perplexity = Math.exp(valLoss())

console.log("perplexity:", round(perplexity, 2), " (out of", chars.length, "characters)")
for (const t of [0.5, 1]) console.log(\`T = \${t}:\`, JSON.stringify(generate("the ", 80, t, 8)))
`,
  tests: `
test("adds exactly n characters", () => expect(generate("the ", 20, 1, 5).length, "length").toBe(24))
test("keeps the prompt", () => expect(generate("once ", 5, 1, 5).startsWith("once "), "starts with the prompt").toBe(true))
test("top-1 is deterministic", () => expect(generate("the ", 15, 1, 1), "first run").toBe(generate("the ", 15, 1, 1)))
test("perplexity is e^loss, well under the vocabulary size", () => {
  expect(perplexity, "perplexity").toBeCloseTo(Math.exp(valLoss()))
  expect(perplexity, "perplexity").toBeLessThan(10)
})
`,
}
